<script setup lang="ts">
import { onMounted } from 'vue'
import { formatCurrency } from '../../../utilities/helpers'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import {
    AgioDisagioType,
    AssetCategory,
    ClearingSystem,
    getCampaignData,
} from '../../../utilities/investment-process/useCampaign'
import BaseInputText from '../../base/BaseInputText.vue'
import BaseButtonMuted from '../../base/BaseButtonMuted.vue'
import { computed, ref } from 'vue'
import { CouponCode } from '../../../typings'
import { storeToRefs } from 'pinia'
import { XIcon } from '@heroicons/vue/outline'
import Modal from '../../dialogs/Modal.vue'
import WalletAmountSelect from './WalletAmountSelect.vue'
import { useWalletState } from '../../../utilities/account/useWallet'
import VoucherAmountSelect from './VoucherAmountSelect.vue'
import { usePersonalAccountDataState } from '../../../utilities/account/useAccountDataPersonal'
import { isNaturalProfile } from '../../../utilities/investment-process/useUserProfileType'
import { postOrderValidateVoucher } from '../../../utilities/api'
import { useLegalAccountDataState } from '../../../utilities/account/useAccountDataLegal'
import { getPlatformSettings, isCouponCodeEnabled } from '../../../utilities/usePlatformSettings'
import { notifyError, notifySuccess } from '../../../utilities/useNotifications'
import { i18n } from '../../../localization'
import { useStockInvestmentStore } from '../../../utilities/investment-process/useStockInvestment'
import { getPendingInvestmentCookie } from '../../../utilities/investment-process/usePendingInvestmentProcess'

defineProps<{
    readOnly: boolean
}>()

const walletState = useWalletState()
const personalAccountDataState = usePersonalAccountDataState()
const investmentProcessDataState = useInvestmentProcessDataState()
const { appliedCoupons, walletAmount, voucherAmount, voucherifyVoucherUid, amountToPay, pieces } =
    storeToRefs(investmentProcessDataState)

const stockInvestmentStore = useStockInvestmentStore()
const pendingInvestment = getPendingInvestmentCookie()

function newCouponCode(code: string): CouponCode {
    return {
        code: code,
        amount: 10,
    }
}

const applyCouponLoading = ref(false)
const couponCode = ref('')
const couponCodeError = ref(false)
const activePaymentAccount = computed(() => getPlatformSettings().active_payment_account)

async function applyCouponCode() {
    try {
        applyCouponLoading.value = true
        const { data } = await postOrderValidateVoucher({
            voucher_code: couponCode.value,
            order_amount: useInvestmentProcessDataState().investmentAmount,
            asset_uid: getCampaignData().asset_data.asset_uid,
            asset_var_uid: getCampaignData().asset_data.asset_var_uid,
            account_uid: isNaturalProfile()
                ? usePersonalAccountDataState().accountId
                : useLegalAccountDataState().accountId,
        })
        if (data.discount_amount > amountToPay.value) {
            notifyError(i18n.global.t('investment_process.discount_code_higher_than_amount'))
            return
        }
        investmentProcessDataState.appliedCoupons.push(data)
        notifySuccess(i18n.global.t('investment_process.discount_code_applied_successfully'))
        couponCodeError.value = false
    } catch (e) {
        couponCodeError.value = true
        setTimeout(() => {
            couponCodeError.value = false
        }, 1000)
        console.error(e)
    } finally {
        couponCode.value = ''
        applyCouponLoading.value = false
    }
}

function removeCouponCode(index) {
    investmentProcessDataState.appliedCoupons.splice(index, 1)
}

const voucherAmountModalOpen = ref(false)
const walletAmountModalOpen = ref(false)

const maxVoucherAmount = computed(() => {
    let maxBalance = 0
    personalAccountDataState?.naturalProfile?.vouchers.forEach((voucher) => {
        if (voucher.gift_card_balance > maxBalance) {
            maxBalance = voucher.gift_card_balance
        }
    })
    return maxBalance
})

onMounted(() => {
    calculateAgioDisagio()
})

function calculateAgioDisagio() {
    let agioFactor = 0
    if (getCampaignData().asset_data.agio_disagio === 1) {
        agioFactor = 1
    } else if (getCampaignData().asset_data.agio_disagio === 2) {
        agioFactor = -1
    }

    let agioAmount = 0
    // calculate agio according to % or fixed type
    if (getCampaignData().asset_data.agio_disagio_type === 0) {
        agioAmount =
            useInvestmentProcessDataState().investmentAmount *
            ((getCampaignData().asset_data.agio_disagio_rate / 100) * agioFactor)
    } else if (getCampaignData().asset_data.agio_disagio_type === 1) {
        agioAmount = getCampaignData().asset_data.agio_disagio_rate * agioFactor
    } else if (
        getCampaignData().asset_data.agio_disagio_type === AgioDisagioType.FIXED_VALUE_PER_SHARE &&
        getCampaignData().asset_data.category === AssetCategory.STOCK_EMISSION
    ) {
        agioAmount =
            (getCampaignData().asset_data.agio_disagio_rate as number) *
            useStockInvestmentStore().clientInvestmentInShares *
            agioFactor
    } else if (getCampaignData().asset_data.agio_disagio_type === 2) {
        agioAmount =
            (getCampaignData().asset_data.agio_disagio_rate as number) *
            useInvestmentProcessDataState().pieces *
            agioFactor
    } else {
        agioAmount = 0
    }
    investmentProcessDataState.agioDisagioAmount = agioAmount
}
</script>

<template>
    <div class="rounded-lg">
        <Modal v-model="walletAmountModalOpen" size="sm">
            <WalletAmountSelect v-model="walletAmount" @close="walletAmountModalOpen = false" />
        </Modal>
        <Modal v-if="isNaturalProfile()" v-model="voucherAmountModalOpen" size="sm">
            <VoucherAmountSelect
                v-model:voucher-amount="voucherAmount"
                v-model:selected-voucher-id="voucherifyVoucherUid"
                @close="voucherAmountModalOpen = false" />
        </Modal>
        <h3 class="pb-3 text-base font-bold text-gray-700">{{ $t('investment_process.investment_summary') }}</h3>
        <div class="flow-root">
            <dl class="divide-y divide-gray-200 rounded-lg border border-gray-200 px-5 text-sm">
                <div class="flex-column py-4">
                    <div class="flex items-center justify-between">
                        <dt class="text-gray-600">{{ $t('investment_process.investment_amount') }}</dt>
                        <dd class="font-medium text-gray-900">
                            {{
                                readOnly
                                    ? formatCurrency(
                                          useInvestmentProcessDataState().createdOrder?.total_amount,
                                          getCampaignData().asset_data.currency
                                      )
                                    : formatCurrency(
                                          useInvestmentProcessDataState().investmentAmount,
                                          getCampaignData().asset_data.currency
                                      )
                            }}
                        </dd>
                    </div>
                    <div
                        v-if="getCampaignData().asset_data.clearing_system.name === ClearingSystem.STOCK_EMISSION"
                        class="flex items-center justify-between">
                        <dt class="text-gray-600">{{ $t('investment_process.stock') }}</dt>
                        <dd class="font-medium text-gray-900">
                            {{
                                stockInvestmentStore.clientInvestmentInShares &&
                                stockInvestmentStore.clientInvestmentInShares > 0
                                    ? stockInvestmentStore.clientInvestmentInShares
                                    : pendingInvestment.investment_amount / getCampaignData().asset_data.price_per_stock
                            }}
                        </dd>
                    </div>
                    <div
                        v-if="!readOnly && useInvestmentProcessDataState().agioDisagioAmount"
                        class="flex items-center justify-between">
                        <dt class="text-gray-600">{{ getCampaignData().asset_data.agio_disagio_title }}</dt>
                        <dd class="font-medium text-gray-900">
                            {{ formatCurrency(useInvestmentProcessDataState().agioDisagioAmount) }}
                        </dd>
                    </div>
                    <div
                        v-if="!readOnly && useInvestmentProcessDataState().agioDisagioAmount"
                        class="flex items-center justify-between">
                        <dt class="text-gray-300">{{ getCampaignData().asset_data.agio_disagio_info_text }}</dt>
                    </div>
                    <template v-if="!readOnly && activePaymentAccount === 'lemonway'">
                        <div
                            v-if="walletState.wallet?.retrievable_balance > 0"
                            class="flex items-center justify-between py-4">
                            <dt class="text-gray-600">
                                <span v-if="walletAmount">
                                    {{ $t('investment_process.wallet_amount') }}
                                </span>
                                <button
                                    class="cursor-pointer rounded-full px-3 py-1 text-secondary transition-all hover:bg-secondary-50 hover:text-secondary-600"
                                    :class="{ '-ml-3': !walletAmount }"
                                    @click="walletAmountModalOpen = true">
                                    {{
                                        walletAmount
                                            ? $t('investment_process.update_wallet_amount')
                                            : $t('investment_process.apply_wallet_amount')
                                    }}
                                </button>
                                <span v-if="!walletAmount">
                                    ({{ $t('investment_process.available') }}:
                                    {{
                                        formatCurrency(
                                            walletState.wallet?.retrievable_balance,
                                            getCampaignData().asset_data.currency_string
                                        )
                                    }})
                                </span>
                            </dt>
                            <dd class="font-medium text-green-600">
                                {{ formatCurrency(-walletAmount, getCampaignData().asset_data.currency_string) }}
                            </dd>
                        </div>
                        <div
                            v-if="isNaturalProfile() && personalAccountDataState?.naturalProfile?.vouchers.length > 0"
                            class="flex items-center justify-between py-4">
                            <dt class="text-gray-600">
                                <span v-if="voucherAmount">
                                    {{ $t('investment_process.voucher_amount') }}
                                </span>
                                <button
                                    class="cursor-pointer rounded-full px-3 py-1 text-secondary transition-all hover:bg-secondary-50 hover:text-secondary-600"
                                    :class="{ '-ml-3': !voucherAmount }"
                                    @click="voucherAmountModalOpen = true">
                                    {{
                                        voucherAmount
                                            ? $t('investment_process.update_voucher_amount')
                                            : $t('investment_process.apply_voucher_amount')
                                    }}
                                </button>
                                <span v-if="!voucherAmount">
                                    ({{ $t('investment_process.available') }}:
                                    {{
                                        formatCurrency(maxVoucherAmount, getCampaignData().asset_data.currency_string)
                                    }})
                                </span>
                            </dt>
                            <dd class="font-medium text-green-600">
                                {{
                                    formatCurrency(
                                        -useInvestmentProcessDataState().voucherAmount,
                                        getCampaignData().asset_data.currency_string
                                    )
                                }}
                            </dd>
                        </div>
                        <div v-if="isCouponCodeEnabled()" class="space-y-2 py-8">
                            <div
                                class="flex flex-col items-start justify-between space-y-4 md:flex-row md:items-center md:space-x-8 md:space-y-0">
                                <dt class="text-gray-600">{{ $t('investment_process.discount_code') }}</dt>
                                <dd
                                    class="w-full font-medium text-gray-900 md:w-auto"
                                    :class="{ 'animate-shake': couponCodeError }">
                                    <form @submit.prevent>
                                        <BaseInputText
                                            v-if="appliedCoupons.length === 0"
                                            v-model="couponCode"
                                            :disabled="amountToPay <= 0"
                                            :placeholder="$t('investment_process.discount_code')"
                                            class="-my-1.5 min-w-[250px]">
                                            <template #add-on>
                                                <BaseButtonMuted
                                                    type="submit"
                                                    size="xs"
                                                    :disabled="!couponCode || amountToPay <= 0"
                                                    :loading="applyCouponLoading"
                                                    @click="applyCouponCode">
                                                    {{ $t('investment_process.apply_discount_button') }}
                                                </BaseButtonMuted>
                                            </template>
                                        </BaseInputText>
                                    </form>
                                </dd>
                            </div>
                            <div
                                v-for="(coupon, idx) in investmentProcessDataState.appliedCoupons"
                                :key="coupon.idx"
                                class="flex items-center justify-between">
                                <dt class="text-gray-600">
                                    <div class="flex items-center space-x-1 rounded-md border p-2">
                                        <div>
                                            {{ coupon.voucher_code }}
                                        </div>
                                        <XIcon
                                            class="h-4 w-4 transition-all hover:cursor-pointer hover:text-red-800"
                                            @click="removeCouponCode(idx)" />
                                    </div>
                                </dt>
                                <dd class="font-medium text-green-600">
                                    {{
                                        formatCurrency(
                                            -coupon.discount_amount,
                                            getCampaignData().asset_data.currency_string
                                        )
                                    }}
                                </dd>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div
                            v-if="useInvestmentProcessDataState().createdOrder?.wallet_amount"
                            class="flex items-center justify-between py-4">
                            <dt class="text-gray-600">{{ $t('investment_process.wallet_amount') }}</dt>
                            <dd class="font-medium text-green-600">
                                {{
                                    formatCurrency(
                                        -useInvestmentProcessDataState().createdOrder?.wallet_amount,
                                        getCampaignData().asset_data.currency_string
                                    )
                                }}
                            </dd>
                        </div>
                        <div
                            v-if="useInvestmentProcessDataState().createdOrder?.voucher_amount"
                            class="flex items-center justify-between py-4">
                            <dt class="text-gray-600">{{ $t('investment_process.voucher_amount') }}</dt>
                            <dd class="font-medium text-green-600">
                                {{
                                    formatCurrency(
                                        -useInvestmentProcessDataState().createdOrder?.voucher_amount,
                                        getCampaignData().asset_data.currency_string
                                    )
                                }}
                            </dd>
                        </div>
                        <div
                            v-if="useInvestmentProcessDataState().createdOrder?.discount_voucher_amount"
                            class="flex items-center justify-between py-4">
                            <dt class="text-gray-600">{{ $t('investment_process.discount_code') }}</dt>
                            <dd class="font-medium text-green-600">
                                {{
                                    formatCurrency(
                                        -useInvestmentProcessDataState().createdOrder?.discount_voucher_amount,
                                        getCampaignData().asset_data.currency_string
                                    )
                                }}
                            </dd>
                        </div>
                    </template>
                    <div
                        v-if="
                            getCampaignData().asset_data.needs_contract_type_selection &&
                            getCampaignData().asset_data.clearing_system.name !== ClearingSystem.STOCK_EMISSION
                        "
                        class="flex items-center justify-between py-4">
                        <dt class="text-gray-600">{{ $t('investment_process.contract_type') }}</dt>
                        <dd class="font-medium text-gray-900">
                            {{ useInvestmentProcessDataState().contractType }}
                        </dd>
                    </div>
                    <div
                        v-if="useInvestmentProcessDataState().incentive"
                        class="flex items-center justify-between py-4">
                        <dt class="text-gray-600">{{ $t('investment_process.incentive') }}</dt>
                        <dd class="font-medium text-gray-900">
                            {{ useInvestmentProcessDataState().incentive?.incentives_title }}
                        </dd>
                    </div>
                    <div class="flex items-center justify-between py-4">
                        <dt class="text-base font-semibold text-gray-900">
                            {{ $t('investment_process.amount_to_pay') }}
                        </dt>
                        <dd class="text-base font-semibold text-gray-900">
                            {{
                                readOnly
                                    ? formatCurrency(
                                          useInvestmentProcessDataState().createdOrder?.total_amount_to_pay,
                                          getCampaignData().asset_data.currency_string
                                      )
                                    : formatCurrency(
                                          useInvestmentProcessDataState().amountToPay,
                                          getCampaignData().asset_data.currency_string
                                      )
                            }}
                        </dd>
                    </div>
                </div>
            </dl>
        </div>
    </div>
</template>

<style scoped></style>
